import { useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { IWorldNftTradeResponse } from 'models/store/i-world-nft-trade-response.interface';

import { ROUTES } from 'constants/routes.constant';

import Button from 'components/buttons/Button';
import NftCard from 'components/nftCard/_new/nftCard/NftCard';
import NftCardLabelPanel from 'components/nftCard/_new/nftCardLabelPanel/NftCardLabelPanel';
import { INftCardAsset } from 'components/nftCard/_new/types';

import { parseThousand } from 'utils/parser.utils';

import { ReactComponent as ListCheckmark } from '../../../assets/icons/list-checkmark.svg';
import { ReactComponent as CollapseIcon } from 'assets/icons/collapse-arrow.svg';

import styles from './StoreIntroSection.module.scss';

const features = [
  {
    title: 'Massive Profit Potential',
    text: 'collects 25% royalties from all digital assets in the ecosystem.',
  },
  {
    title: 'Early-bird Pricing',
    text: 'today ’s cost is only a tiny fraction of its real value.',
  },
  {
    title: 'Permanent Utility',
    text: 'unlocks bonuses, features, and premium rewards forever.',
  },
  {
    title: 'Global Scale',
    text: 'one NFT, income from every corner of the planet.',
  },
];

type StoreIntroSectionProps = {
  featuredAsset: INftCardAsset | null;
  rawTradeData: IWorldNftTradeResponse | null;
};

const StoreIntroSection = ({ featuredAsset, rawTradeData }: StoreIntroSectionProps) => {
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  const handleMoreDetailsClick = () => {
    setIsMobileExpanded(true);
  };

  const hasExpectedAnnualYield =
    featuredAsset?.expectedAnnualYield !== '0' && featuredAsset?.expectedAnnualYield !== null;

  if (!rawTradeData) {
    return null;
  }

  return (
    <section className={styles['intro-section']}>
      <div
        className={twMerge(
          styles['background-wrapper'],
          'tw-flex tw-h-full tw-flex-1 tw-flex-col tw-overflow-visible lg:tw-flex-row'
        )}
      >
        <div
          className={twMerge(
            styles['card-container'],
            'tw-flex tw-w-full tw-items-center tw-justify-center tw-overflow-visible lg:tw-mb-0 lg:tw-flex-[40%]'
          )}
        >
          <div className={styles['card-background']}></div>
          <div className={styles['card-section']}>
            <div className="tw-relative tw-mx-auto tw-w-[256px] tw-max-w-sm tw-rounded-2xl tw-border-2 tw-border-solid tw-border-[#a0fafc] tw-p-2">
              <div className="tw-rounded-2xl tw-shadow-[0_0_80px_5px_rgba(34,211,238,0.7)]">
                {featuredAsset ? (
                  <NftCard
                    size="large"
                    asset={featuredAsset}
                    toggleable={false}
                    className="tw-mb-4"
                    disableHover
                    asLinkTo={ROUTES.WORLD_NFT}
                  />
                ) : (
                  <div className="tw-flex tw-h-[300px] tw-items-center tw-justify-center tw-text-gray-500">
                    <span>No asset data available</span>
                  </div>
                )}
              </div>

              {featuredAsset && (
                <NftCardLabelPanel label="Estimated Annual Yield" primary>
                  <span className="tw-leading-4 tw-text-white">
                    {hasExpectedAnnualYield
                      ? `${parseThousand(
                          featuredAsset.expectedAnnualYield ? parseFloat(featuredAsset.expectedAnnualYield) : 0,
                          2
                        )}%`
                      : '-'}
                  </span>
                </NftCardLabelPanel>
              )}
            </div>
          </div>
        </div>
        <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-8 lg:tw-flex-[60%] lg:tw-items-start lg:tw-px-8">
          <div className="tw-relative tw-flex tw-w-full tw-max-w-full tw-flex-col tw-items-start tw-gap-6">
            <header
              className={twMerge(
                'tw-relative tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-3 tw-self-stretch lg:tw-flex',
                isMobileExpanded ? 'tw-flex' : 'tw-hidden'
              )}
            >
              <h1 className="tw-relative !tw-mt-[-1.00px] tw-self-stretch tw-text-left tw-text-3xl tw-font-bold tw-leading-[36px] tw-text-transparent lg:tw-text-left lg:!tw-text-5xl lg:!tw-leading-[48px]">
                <span className="tw-uppercase tw-text-white">INTRODUCING THE </span>
                <span className="tw-uppercase tw-text-[#6dfbff]">WORLD NFT</span>
              </h1>

              <h2 className="tw-relative tw-w-fit tw-text-lg tw-font-semibold tw-leading-[26px] tw-text-white lg:tw-mx-0 lg:tw-whitespace-nowrap lg:!tw-text-[22px] lg:!tw-leading-[30px]">
                The #1 Earning Digital Asset in Reality Metaverse
              </h2>
            </header>

            <p
              className={twMerge(
                'tw-relative tw-self-stretch tw-text-left tw-text-lg tw-font-medium tw-leading-[26px] tw-text-white lg:tw-block lg:tw-text-left lg:tw-text-[22px] lg:tw-leading-[30px]',
                isMobileExpanded ? 'tw-block' : 'tw-hidden'
              )}
            >
              Own the NFT that rules them all: global royalties, endless utility, and airdrops worth far more than the
              entry price. Early buyers before Oct 9 lock in the biggest rewards.
            </p>

            <div className="tw-relative tw-flex tw-w-full tw-max-w-full tw-flex-col tw-items-center tw-gap-[30px] lg:tw-w-[458px] lg:tw-items-start">
              <div
                className={twMerge(
                  'tw-relative tw-flex tw-w-full tw-items-start tw-gap-10 lg:tw-flex',
                  isMobileExpanded ? 'tw-flex' : 'tw-hidden'
                )}
              >
                <ul className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-2 tw-p-0">
                  {features.map((feature, index) => (
                    <li
                      key={index}
                      className="tw-relative tw-flex tw-w-full tw-items-center tw-gap-4 tw-rounded-[100px]"
                    >
                      <ListCheckmark className="tw-relative tw-h-4 tw-w-4 tw-text-white" />
                      <span className="tw-relative tw-flex-1 tw-text-left tw-text-lg tw-font-semibold tw-leading-6 tw-tracking-[-0.40px] tw-text-[#6dfbff] lg:tw-text-xl">
                        {feature.title} <span className="tw-text-white"> {'— ' + feature.text} </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="tw-flex tw-w-full tw-flex-row-reverse tw-items-center tw-justify-center tw-gap-4 lg:tw-flex-row">
                <Link to={ROUTES.WORLD_NFT}>
                  <Button size="medium" color="white-outline" text={'Discover now'} className="tw-flex-1" />
                </Link>
              </div>

              {/* MORE DETAILS button - only visible on mobile when not expanded */}
              <div
                className={twMerge(
                  'tw-flex tw-w-full tw-justify-center lg:tw-hidden',
                  isMobileExpanded ? 'tw-hidden' : 'tw-flex'
                )}
              >
                <Button
                  size="medium"
                  color="white-outline"
                  text="MORE DETAILS"
                  onClick={handleMoreDetailsClick}
                  className="tw-w-full tw-max-w-[190px]"
                  iconComponentRight={
                    <CollapseIcon className="more-details-icon tw-ml-2 tw-h-4 tw-w-4 tw-text-white" />
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoreIntroSection;
